import { FaTimes } from "react-icons/fa";
import { CheckIcon } from "@heroicons/react/20/solid";
import { Sparkles, ShieldCheck, Package } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PermissionsModal({
  isOpen,
  onClose,
  permissionsByCategory = {},
  categoryLabels = {},
}) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  if (!isOpen) return null;

  const isEmpty = Object.keys(permissionsByCategory).length === 0;

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes itemIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatBlob {
          0%,100% { transform: translate3d(0,0,0) rotate(30deg); }
          50% { transform: translate3d(-20px,-30px,0) rotate(35deg); }
        }
        @keyframes pulseGlow {
          0%,100% { box-shadow: 0 0 0 0 rgba(251,191,36,.4); }
          50% { box-shadow: 0 0 0 10px rgba(251,191,36,0); }
        }
        .modal-backdrop-anim { animation: fadeIn .25s ease-out; }
        .modal-panel-anim { animation: modalIn .4s cubic-bezier(.2,.9,.3,1.15); }
        .modal-item-anim { animation: itemIn .4s ease-out backwards; }
        .modal-blob { animation: floatBlob 14s ease-in-out infinite; }
        .modal-badge-pulse { animation: pulseGlow 2.5s ease-in-out infinite; }
      `}</style>

      <div
        className="modal-backdrop-anim mt-16 fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/50 dark:!bg-black/70 backdrop-blur-md"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="permissions-modal-title"
      >
        {/* Blob های تزئینی */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="modal-blob absolute -top-40 -right-20 w-[28rem] h-[28rem] rounded-full bg-gradient-to-tr from-amber-400/20 via-teal-400/15 to-teal-700/20 dark:!from-amber-500/10 dark:!via-teal-500/10 dark:!to-teal-900/20 blur-3xl" />
          <div
            className="modal-blob absolute -bottom-40 -left-20 w-[24rem] h-[24rem] rounded-full bg-gradient-to-tr from-teal-700/20 via-teal-500/15 to-amber-400/20 dark:!from-teal-900/20 dark:!via-teal-700/10 dark:!to-amber-500/10 blur-3xl"
            style={{ animationDelay: "1s" }}
          />
        </div>

        {/* Panel */}
        <div
          className="
            modal-panel-anim relative
            w-full max-w-lg max-h-[85vh] overflow-hidden
            rounded-3xl
            bg-white dark:!bg-slate-900
            border border-teal-500/20 dark:!border-teal-400/15
            shadow-[0_25px_70px_-20px_rgba(15,118,110,0.5)]
            dark:!shadow-[0_25px_70px_-20px_rgba(0,0,0,0.9)]
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* خط طلایی بالا */}
          <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-l from-transparent via-amber-400/90 to-transparent z-10" />

          {/* گلوی تزئینی گوشه بالا */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 dark:!bg-amber-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 dark:!bg-teal-500/10 blur-3xl" />

          {/* ============ Header ============ */}
          <div className="relative flex items-center justify-between gap-3 px-5 py-4 border-b border-teal-500/10 dark:!border-teal-400/10">
            <div className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 shadow-[0_8px_24px_-8px_rgba(251,191,36,0.8)] border border-amber-200/60 dark:!border-amber-400/30">
                <Package className="h-5 w-5 text-white drop-shadow" />
                <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-100" />
              </span>

              <div className="min-w-0">
                <h3
                  id="permissions-modal-title"
                  className="text-base sm:text-lg font-extrabold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:!from-amber-200 dark:!via-teal-300 dark:!to-teal-400 bg-clip-text text-transparent truncate"
                >
                  {t("permissionsModal.title")}
                </h3>
                <p className="text-[10px] sm:text-[11px] text-slate-500 dark:!text-slate-400 mt-0.5 truncate">
                  {t("permissionsModal.subtitle")}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              aria-label={t("permissionsModal.close")}
              className="
                group shrink-0 h-9 w-9 flex items-center justify-center rounded-xl
                text-slate-400 dark:!text-slate-500
                bg-slate-100/60 hover:bg-rose-500/15 dark:!bg-white/5 dark:hover:!bg-rose-500/20
                hover:text-rose-500 dark:hover:!text-rose-400
                border border-slate-200/60 dark:!border-white/10 hover:border-rose-400/40
                transition-all duration-300
                hover:rotate-90 hover:scale-110 active:scale-95
              "
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* ============ Body ============ */}
          <div className="relative max-h-[calc(85vh-88px)] overflow-y-auto custom-scrollbar p-5">

            {/* Empty state */}
            {isEmpty && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="relative mb-4">
                  <span className="absolute inset-0 -m-2 rounded-full bg-amber-400/15 animate-ping" style={{ animationDuration: "2s" }} />
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-amber-300/20 to-teal-500/15 border border-amber-300/40 dark:!border-amber-400/20 flex items-center justify-center">
                    <CheckIcon className="h-7 w-7 text-amber-500 dark:!text-amber-300" />
                  </div>
                </div>
                <p className="text-sm font-bold text-slate-600 dark:!text-slate-300">
                  {t("permissionsModal.emptyTitle")}
                </p>
                <p className="text-[11px] text-slate-400 dark:!text-slate-500 mt-1">
                  {t("permissionsModal.emptySubtitle")}
                </p>
              </div>
            )}

            {/* Categories */}
            <ul className="space-y-4">
              {Object.entries(permissionsByCategory).map(
                ([category, items], catIdx) => {
                  if (!items?.length) return null;

                  return (
                    <li
                      key={category}
                      className="modal-item-anim"
                      style={{ animationDelay: `${catIdx * 0.08}s` }}
                    >
                      {/* Category Header */}
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="shrink-0 flex items-center justify-center w-7 h-7 rounded-xl bg-gradient-to-br from-amber-300/50 to-teal-400/25 border border-amber-300/50 dark:!border-amber-400/30 shadow-[0_4px_12px_-4px_rgba(251,191,36,0.5)]">
                          <CheckIcon className="h-4 w-4 text-amber-700 dark:!text-amber-200" />
                        </span>
                        <h4 className="text-sm sm:text-[15px] font-extrabold text-slate-800 dark:!text-amber-100">
                          {categoryLabels[category] || category}
                        </h4>
                        <span className="shrink-0 h-px flex-1 bg-gradient-to-l from-teal-400/30 to-transparent" />
                        <span className={`shrink-0 text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-lg bg-teal-500/10 dark:!bg-teal-400/10 border border-teal-500/20 dark:!border-teal-400/20 text-teal-700 dark:!text-teal-300 ${isEnglish && 'font-sans'}`}>
                          {items.length.toLocaleString(isEnglish ? "en-US" : "fa-IR")}
                        </span>
                      </div>

                      {/* Items */}
                      <ul className="mr-3 sm:mr-4 space-y-1 text-sm rounded-2xl p-3 sm:p-4 border border-teal-500/15 dark:!border-teal-400/10 bg-slate-50/60 dark:!bg-slate-950/40 backdrop-blur-sm">
                        {items.map((item, idx) => (
                          <li
                            key={item.id}
                            className="
                              modal-item-anim group
                              flex items-center gap-2.5
                              py-1.5 px-2 rounded-lg
                              transition-all duration-200
                              hover:bg-amber-300/10 dark:!hover:bg-amber-400/5
                              hover:translate-x-[-2px]
                            "
                            style={{
                              animationDelay: `${catIdx * 0.08 + idx * 0.04}s`,
                            }}
                          >
                            <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-amber-400 to-teal-500 shadow-[0_0_8px_rgba(251,191,36,0.7)] group-hover:scale-150 transition-transform duration-200" />
                            <span className="text-xs sm:text-sm text-slate-600 dark:!text-slate-300 group-hover:text-amber-700 dark:!group-hover:text-amber-200 transition-colors">
                              {item.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                },
              )}
            </ul>

            {/* Trust line */}
            {!isEmpty && (
              <div className="mt-6 pt-4 border-t border-teal-500/10 dark:!border-teal-400/10 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:!text-slate-500">
                <ShieldCheck size={11} className="text-emerald-500/70" />
                {t("permissionsModal.trustLine")}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(251,191,36,0.9));
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
      `}</style>
    </>
  );
}